// Frontend-only notification store. Backed by localStorage today;
// swap the implementation for an API call later — the public surface stays the same.

const STORAGE_KEY = 'notifications';
const EVENT = 'notifications:change';

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const write = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent(EVENT));
};

export const NOTIFICATION_CATEGORIES = ['booking', 'review', 'payment', 'account', 'promotion', 'system'];

export const getNotifications = () => read();

export const getUnreadCount = () => read().filter(n => !n.readAt).length;

export const addNotification = ({ type = 'system', category = 'system', title, body, link, meta }) => {
  if (!title) return null;
  const next = read();
  const n = {
    id: `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    type,
    category: NOTIFICATION_CATEGORIES.includes(category) ? category : 'system',
    title,
    body: body || '',
    link: link || null,
    meta: meta || null,
    readAt: null,
    createdAt: new Date().toISOString(),
  };
  next.unshift(n);
  // Cap at 200 to avoid unbounded growth
  write(next.slice(0, 200));
  return n;
};

export const markAsRead = (id) => {
  const list = read().map(n => n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n);
  write(list);
};

export const markAsUnread = (id) => {
  const list = read().map(n => n.id === id ? { ...n, readAt: null } : n);
  write(list);
};

export const markAllAsRead = () => {
  const now = new Date().toISOString();
  const list = read().map(n => n.readAt ? n : { ...n, readAt: now });
  write(list);
};

export const deleteNotification = (id) => {
  write(read().filter(n => n.id !== id));
};

export const clearAll = () => {
  write([]);
};

export const subscribe = (cb) => {
  const handler = () => cb(read());
  window.addEventListener(EVENT, handler);
  // also listen to cross-tab changes
  const storageHandler = (e) => { if (e.key === STORAGE_KEY) cb(read()); };
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
};

// Convenience seeders so the UI looks alive in dev. Only seeds if empty.
export const seedDevNotifications = () => {
  if (read().length > 0) return;
  const now = Date.now();
  const sample = [
    { type: 'success', category: 'booking', title: 'Booking confirmed', body: 'Your appointment with Glow Studios is confirmed for Saturday at 2 PM.', link: '/bookings', minutesAgo: 15 },
    { type: 'info', category: 'review', title: 'New review request', body: 'How was your visit to Crown Cuts? Leave a review to help others.', link: '/my-reviews', minutesAgo: 90 },
    { type: 'warning', category: 'payment', title: 'Payment pending', body: 'Your mobile money payment is still being processed.', link: '/transactions', minutesAgo: 240 },
    { type: 'info', category: 'promotion', title: '20% off facials this week', body: 'Selected providers are running facial promos. Tap to browse.', link: '/search', minutesAgo: 60 * 24 },
    { type: 'success', category: 'account', title: 'Password updated', body: 'Your password was changed successfully.', minutesAgo: 60 * 24 * 3 },
  ];
  const seeded = sample.map(s => ({
    id: `n_seed_${Math.random().toString(36).slice(2, 9)}`,
    type: s.type,
    category: s.category,
    title: s.title,
    body: s.body,
    link: s.link || null,
    meta: null,
    readAt: null,
    createdAt: new Date(now - s.minutesAgo * 60 * 1000).toISOString(),
  }));
  write(seeded);
};
